import { Box, Skeleton, useToast } from '@chakra-ui/react';
import React, { useCallback } from 'react';

import {
  GetAllPlaidInstitutionAccountsDocumentGQLType,
  GetPlaidLinkTokenDocumentGQLType,
  useConnectPlaidItemMutation,
  useGetAllPlaidInstitutionAccountsQuery,
  useGetPlaidLinkTokenQuery,
  useReconnectPlaidItemMutation,
} from '../requests/plaid.gql.generated';
import { Institution } from './Institution';
import { LinkModal } from './LinkModal';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import {
  GetBankAccountsByCreditFacilityIdDocumentGQLType,
  GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
} from '~/packages/bankAccounts/requests/bankAccounts.gql.generated';
import { errorToastConfig } from '~/packages/common-frontend/constants';

export const PlaidContainer = ({
  cashBalancePreference,
}: {
  cashBalancePreference: 'current' | 'available';
}) => {
  const toast = useToast();
  const {
    data: plaidLinkData,
    loading: plaidLinkLoading,
  } = useGetPlaidLinkTokenQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const [
    connectPlaidItemMutate,
    { loading: connectPlaidItemLoading },
  ] = useConnectPlaidItemMutation({
    refetchQueries: [
      GetPlaidLinkTokenDocumentGQLType,
      GetBankAccountsByCreditFacilityIdDocumentGQLType,
      GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
      GetAllPlaidInstitutionAccountsDocumentGQLType,
    ],
  });

  const handleTokenExchange = useCallback(
    async (publicToken: string, plaidInstitutionId: string) => {
      return connectPlaidItemMutate({
        variables: {
          publicToken,
          plaidInstitutionId,
        },
      });
    },
    [connectPlaidItemMutate],
  );

  const [
    reconnectPlaidItem,
    { loading: reconnectPlaidItemLoading },
  ] = useReconnectPlaidItemMutation({
    refetchQueries: [
      GetPlaidLinkTokenDocumentGQLType,
      GetAllPlaidInstitutionAccountsDocumentGQLType,
    ],
  });

  const handleReconnectPlaidItem = useCallback(
    async (plaidItemId: string) => {
      return reconnectPlaidItem({
        variables: {
          plaidItemId,
        },
      });
    },
    [reconnectPlaidItem],
  );

  const {
    data: allPlaidInstitutionAccountsData,
    loading: allPlaidInstitutionAccountsLoading,
  } = useGetAllPlaidInstitutionAccountsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  if (
    connectPlaidItemLoading ||
    reconnectPlaidItemLoading ||
    plaidLinkLoading ||
    !plaidLinkData ||
    allPlaidInstitutionAccountsLoading ||
    !allPlaidInstitutionAccountsData
  ) {
    return <Skeleton height="600px" data-testid="plaid-container-loading" />;
  }

  const {
    token,
    needsUpdate,
    institutionName,
    plaidItemId,
  } = plaidLinkData.plaidLinkToken;
  const { allPlaidInstitutionAccounts } = allPlaidInstitutionAccountsData;

  return (
    <Box>
      <LinkModal
        linkToken={token}
        needsUpdate={needsUpdate}
        institutionName={institutionName}
        plaidItemId={plaidItemId}
        handleTokenExchange={handleTokenExchange}
        handleReconnectPlaidItem={handleReconnectPlaidItem}
      />

      <Box mt="2rem">
        {allPlaidInstitutionAccounts.map((institutionWithAccounts) => {
          return (
            <Institution
              cashBalancePreference={cashBalancePreference}
              key={`key-${Math.random()}`}
              institutionWithAccounts={institutionWithAccounts}
            />
          );
        })}
      </Box>
    </Box>
  );
};
